import {
  useLibraryPlacementRegistryQuery,
  pathwaySelectionPolicyOf,
} from "@/api/library";
import { dynamicCapacity } from "@/lib/pathwayFamilies";
import type { SectionAdoptedItemView } from "@/lib/sectionAdoption";
import { Notice } from "@/ui/Notice";

/**
 * What this project's Pathway pins are selected AS at runtime, and how much of
 * the server's Dynamic allowance they use (#643, audit PW-PRJ-03 / PW-PRJ-10).
 *
 * ## The concept the audit found missing, added the only way the contract
 * permits
 *
 * PW-PRJ-03 records "Always assessed/Dynamic/max-per-patient" against "entire
 * concept absent", and PW-PRJ-10 records a reference control that "declares max
 * 2 but persists 3". Both are answered here, and neither is answered with a
 * control:
 *
 *   * **Always/Dynamic is DISPLAYED, never elected.** `PathwaySelection` carries
 *     `authority: "server"` as its one legal value, and
 *     `PathwaySelectionPolicy` publishes no request-shaped mode or order field —
 *     "A project submits exact immutable pins; preflight reads the effective
 *     engine-owned registration, derives this state, and persists it with those
 *     pins." A switch here would be a control with no request behind it, which
 *     ADR 0001's no-dead-controls rule forbids and which #516 settled by
 *     deleting such surfaces rather than disabling them. So the surface states
 *     what the server decided, and says where the decision came from.
 *   * **The maximum is READ, never restated.** `maxDynamic` comes from the
 *     placement registry — the same `MAX_DYNAMIC_PATHWAYS` `adoption_preflight`
 *     refuses against — so the number on screen and the number that refuses the
 *     write are one value. A literal three in the browser is exactly the defect
 *     PW-PRJ-10 names, one version bump away.
 *
 * ## Why it is not a client-side rule
 *
 * The count is over selections the SERVER reported on this project's pins, not
 * over what this browser thinks is adoptable. Nothing here decides whether a
 * further pin would fit: that is preflight's answer, it arrives as
 * `placement.cardinality_exceeded` on the refused write, and the adoption
 * dialogs already render the service's typed refusal. This surface reports
 * consumption; it does not gate.
 *
 * ## The states it distinguishes
 *
 * Four, and each is a different fact:
 *
 *   * the registry has not been read, so no cap is shown at all;
 *   * the cap is known and the project is under it;
 *   * the cap is known and the project is AT it — stated because the next pin
 *     is the one that will be refused;
 *   * the reported count EXCEEDS the cap. That is not a client verdict on the
 *     project but a disagreement between two server answers read at different
 *     moments — a registry version that lowered the cap over a pin set
 *     persisted under the old one — and it is surfaced rather than clamped,
 *     because "4 of 3" is the state an operator needs to see.
 */
export function PathwaySelectionSummary({
  items,
}: {
  /**
   * The section's adopted items, exactly as `deriveSectionAdoption` produced
   * them. Only the Pathways section carries selections at all; the component is
   * rendered by its block and reads `selection` straight through.
   */
  items: readonly SectionAdoptedItemView[];
}) {
  const registry = useLibraryPlacementRegistryQuery();
  const policy = pathwaySelectionPolicyOf(registry.data);
  const capacity = dynamicCapacity(
    items.map((item) => item.selection),
    policy,
  );

  const always = items.filter((item) => item.selection?.mode === "always");
  const dynamic = items.filter((item) => item.selection?.mode === "dynamic");
  const unselected = items.filter((item) => item.selection === null);

  if (items.length === 0) {
    return null;
  }

  return (
    <section
      aria-label="Runtime selection"
      className="mt-3 rounded-md border border-border px-3 py-2"
    >
      <h4 className="text-sm font-medium text-foreground">Runtime selection</h4>
      <p className="mt-1 text-xs text-muted-foreground">
        Decided by the Editor Service from the pinned engine&apos;s own
        registration, and stored with these pins. It is not chosen in this
        editor.
      </p>

      <dl className="mt-2 space-y-1 text-xs text-muted-foreground">
        <div className="flex flex-wrap gap-x-2">
          <dt className="font-medium text-foreground">Always assessed</dt>
          <dd>
            {always.length === 0
              ? "None of this section's pins."
              : `${always.length} of this section's pins.`}
          </dd>
        </div>
        <div className="flex flex-wrap gap-x-2">
          <dt className="font-medium text-foreground">Dynamic</dt>
          <dd>
            {capacity === null
              ? // NO CAP IS SHOWN WHEN NONE COULD BE READ. The count is still a
                // fact; "of N" would be a claim about policy this browser could
                // not source, and the server's refusal does not depend on it.
                `${dynamic.length} of this section's pins. The maximum is not shown: the Editor Service's placement registry could not be read.`
              : `${capacity.used} of at most ${capacity.max} allowed by the Editor Service.`}
          </dd>
        </div>
        {unselected.length > 0 ? (
          <div className="flex flex-wrap gap-x-2">
            <dt className="font-medium text-foreground">No runtime selection</dt>
            <dd>
              {unselected.length}{" "}
              {unselected.length === 1 ? "pin is" : "pins are"} reported without
              one, which the contract states for a member that is summary-only
              rather than an active selection.
            </dd>
          </div>
        ) : null}
      </dl>

      {capacity?.overCapacity ? (
        <Notice tone="destructive" role="alert" className="mt-2">
          The Editor Service reports {capacity.used} Dynamic selections on this
          project while its placement registry allows {capacity.max}. Both are
          server answers; this editor changes neither. A further pin will be
          refused until they agree.
        </Notice>
      ) : capacity?.atCapacity ? (
        <p className="mt-2 text-xs text-foreground">
          This project holds every Dynamic selection the Editor Service allows.
          Adopting another pathway that resolves to Dynamic is refused until one
          is removed.
        </p>
      ) : null}
    </section>
  );
}
